import { FormlyFieldConfig } from '@ngx-formly/core';

export enum PluginContexts {
  Global = 'global',
  Page = 'page',
  Element = 'element',
  Variant = 'variant',
}

/**
 * Represents the Survey Plugin Configuration record.
 */
export interface SurveyPluginUsage {
  id: string;
  plugin_id: string | null;
  element_id: number | null;
  page_id: number | null;
  survey_id: number | null;

  /**
   * The configuration data stored as JSONB.
   * Using Record<string, any> to represent the object structure.
   */
  plugin_values: Record<string, any>;

  /**
   * Defines the scope of the plugin.
   * Based on your SQL comment: 'global' | 'page' | 'element'
   */
  plugin_type: PluginContexts;
}

/**
 * Represents the definition of a Plugin as stored in the DB
 */
export interface ISurveyPlugin {
  id: string; // Unique slug (e.g., 'audio-recorder')
  name: string; // Display name for Admin UI
  version: string;

  // 1. Admin Portal Controls (Storybook-style)
  /**
   * configSchema now uses standard JSON Forms types.
   */
  config_schema: IPluginConfigSchema;

  // 2. Saved configuration values for this instance
  // config?: Record<string, any>;

  // 3. Variables this plugin contributes to the survey results
  variable_schema: IPluginVarConf;

  // 4. UI Component (Optional)
  html?: string;

  // 5. Logic Hooks (Javascript strings)
  hooks: {
    onPageInit?: string; // Called on component ngOnInit
    onDataChange?: string; // Called when registry() changes
    onBeforeSubmit?: string; // Called during onSubmit loop
    onCleanup?: string; // Called on ngOnDestroy
  };

  is_global: boolean;
  is_page: boolean;
  is_element: boolean;
}

export interface IPluginConfigSchema extends FormlyFieldConfig {}

/**
 * Defines variables that will be merged into the final answerObject
 */
export interface IPluginVariable {
  key: string;
  type: 'string' | 'number' | 'boolean' | 'object';
  description?: string;
}

export interface IPluginVarConf {
  // Use conf overides if any property of the element config needs to be overwritten by values in the schema
  confOverides: string[];
}

export interface IPluginExecutionContext {
  // Config & Data
  config: Record<string, any>; // User settings from Admin Portal
  registry: Record<string, any>; // Current page registry (readonly)
  container: any; // The plugin host's DOM element
  pageId: string | undefined;
  survey_context: SurveyContext;



  // Methods to interact with Survey
  setInvalid: (msg: string) => void;
  getAnswer: (id: string) => any; // Replace 'any' with your core Answer type if possible
  patchData: (patch: Partial<any>) => void; // Replace 'any' with your form/value data type
  pluginData: () => any;

  // State Persistence (Allows variables created in Init to live until Submit)
  state: any;
}

export type PluginHookName =
  | 'onPageInit'
  | 'onDataChange'
  | 'onBeforeSubmit'
  | 'onCleanup';


// Replace 'any' with your actual Config/Registry types if available
export interface PluginConfig {
  [key: string]: any;
}

export interface SurveyContext {
  survey_id: string | number; // Adjust based on your ID types
  survey_session_id: string | number;
}

export interface SurveyPluginUsageWithDefinition extends SurveyPluginUsage {
  plugin: ISurveyPlugin | null;
}
